/**
 * IntakeReviewContent — Stage 1 (Intake) composite for the accountant Case
 * Workspace.
 *
 * Intake is the case entry point: the accountant reviews WHAT documents came
 * in (and how they were extracted) and HOW GOOD that data is. Rather than
 * rebuild either surface, this thin composite FRAMES two existing,
 * self-fetching panels — zero net-new dashboards:
 *
 *   - Extracted Documents → <ExtractedDocumentsPanel caseId={reportId} />
 *       (self-fetches /api/v1/supporting-documents/case/:id)
 *   - Data Quality        → <DataQualityReportPanel claimId={reportId} />
 *       (self-fetches the data-quality report set)
 *
 * A segmented sub-nav switches between the two peer surfaces, reusing the
 * approved wayfinding language (active = 2px orange underline, idle = dim
 * grey, no fill) so "selected" reads the same as the stage strip and the P&L
 * year toggle. One orange accent (the active underline); everything else
 * hueless.
 *
 * Props mirror the other framed-stage content components:
 *   - reportId  falls back to useParams (standalone-route safe)
 *   - showHeader gates the page-header block so the panel renders bare inside
 *     the workspace stage (no double-chrome).
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ClipboardList, FileStack, ShieldCheck } from 'lucide-react';
import ExtractedDocumentsPanel from '../ExtractedDocumentsPanel';
import DataQualityReportPanel from '../data-quality/DataQualityReportPanel';
import CaseDocumentInventory from './CaseDocumentInventory';
import EmptyState from './EmptyState';

export interface IntakeReviewContentProps {
  reportId?: string;
  showHeader?: boolean;
}

type IntakeSubTab = 'documents' | 'quality';

const SUB_TABS: { id: IntakeSubTab; label: string; icon: React.ElementType }[] = [
  { id: 'documents', label: 'Extracted Documents', icon: FileStack },
  { id: 'quality', label: 'Data Quality', icon: ShieldCheck },
];

export function IntakeReviewContent({
  reportId: reportIdProp,
  showHeader = true,
}: IntakeReviewContentProps) {
  const { reportId: reportIdParam } = useParams<{ reportId: string }>();
  const reportId = reportIdProp ?? reportIdParam;
  const [subTab, setSubTab] = useState<IntakeSubTab>('documents');

  if (!reportId) {
    return (
      <div className="p-6 lg:p-8">
        <EmptyState
          icon={ClipboardList}
          label="No case selected"
          hint="Open a case to review its intake documents and data quality."
        />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto">
      {showHeader && (
        <div className="mb-6">
          <div className="cs-eyebrow flex items-center gap-2 mb-1">
            <span>ACCOUNTANT REVIEW</span>
            <span>•</span>
            <span>INTAKE</span>
          </div>
          <h1 className="cs-display">Case Intake</h1>
        </div>
      )}

      {/* Segmented sub-nav — approved wayfinding: active = 2px orange
          underline, idle = dim grey text, no fill. One orange accent. */}
      <div
        role="tablist"
        aria-label="Intake sections"
        className="flex items-center gap-1 mb-6"
        style={{ borderBottom: '1px solid var(--color-hairline)' }}
      >
        {SUB_TABS.map(({ id, label, icon: Icon }) => {
          const active = subTab === id;
          return (
            <button
              key={id}
              role="tab"
              aria-selected={active}
              onClick={() => setSubTab(id)}
              data-testid={`intake-subtab-${id}`}
              className="cs-label flex items-center gap-2 px-3 py-2 rounded-[2px] transition-colors"
              style={{
                color: active ? 'var(--color-text-secondary)' : 'var(--color-text-dim)',
                borderBottom: active
                  ? '2px solid var(--color-brand)'
                  : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          );
        })}
      </div>

      <div data-testid={`intake-panel-${subTab}`}>
        {subTab === 'documents' ? (
          <div className="space-y-8">
            {/* True case document inventory — every uploaded file, grouped
                and with parse status (was previously hidden: the accountant
                only saw the narrow 3-doc structured-extraction panel below). */}
            <CaseDocumentInventory caseId={reportId} />
            <div>
              <h3
                className="cs-label mb-3"
                style={{ color: 'var(--color-text-muted)' }}
              >
                Structured extraction — policy · lease · loan terms
              </h3>
              <ExtractedDocumentsPanel caseId={reportId} />
            </div>
          </div>
        ) : (
          <DataQualityReportPanel claimId={reportId} />
        )}
      </div>
    </div>
  );
}

export default IntakeReviewContent;
